import React, { useEffect, useState, useCallback } from "react";
import { client, formatApiError } from "@/lib/api";
import { DynamicForm } from "@/admin/DynamicForm";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, Eye, EyeOff, Loader2 } from "lucide-react";
import { toast } from "sonner";

const StatusBadge = ({ status }) => {
  const map = {
    published: "bg-green-100 text-green-800",
    draft: "bg-slate-100 text-slate-600",
    review: "bg-amber-100 text-amber-800",
    scheduled: "bg-blue-100 text-blue-800",
  };
  return <span className={`rounded-sm px-2 py-0.5 text-xs font-medium capitalize ${map[status] || map.draft}`}>{status || "draft"}</span>;
};

export const ResourceManager = ({ resource, config }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const base = `/admin/resources/${resource}`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await client.get(base);
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [base]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setValues({ status: "draft" }); setOpen(true); };
  const openEdit = (item) => { setEditing(item); setValues({ ...item }); setOpen(true); };

  const save = async () => {
    const missing = config.fields.filter((f) => f.required && !values[f.name]);
    if (missing.length) { toast.error(`Please fill: ${missing.map((f) => f.label).join(", ")}`); return; }
    setSaving(true);
    try {
      if (editing) await client.put(`${base}/${editing.id}`, values);
      else await client.post(base, values);
      toast.success(editing ? "Updated" : "Created");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await client.delete(`${base}/${deleteId}`);
      toast.success("Deleted");
      setDeleteId(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const toggleStatus = async (item) => {
    const next = item.status === "published" ? "draft" : "published";
    try {
      await client.patch(`${base}/${item.id}/status`, { status: next });
      toast.success(next === "published" ? "Published" : "Unpublished");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const move = async (index, dir) => {
    const next = [...items];
    const j = index + dir;
    if (j < 0 || j >= next.length) return;
    [next[index], next[j]] = [next[j], next[index]];
    setItems(next);
    try {
      await client.post(`${base}/reorder`, { ids: next.map((x) => x.id) });
    } catch (e) {
      toast.error("Reorder failed");
      load();
    }
  };

  const cellValue = (item, key) => {
    const v = item[key];
    if (key === "status") return <StatusBadge status={v} />;
    if (key === "featured") return v ? <Badge className="rounded-sm bg-brand-orange">Featured</Badge> : <span className="text-muted-foreground">—</span>;
    if (typeof v === "boolean") return v ? "Yes" : "No";
    return <span className="text-navy">{v || <span className="text-muted-foreground">—</span>}</span>;
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-navy">{config.title}</h1>
          <p className="text-sm text-muted-foreground">{items.length} item{items.length !== 1 ? "s" : ""}</p>
        </div>
        <Button data-testid={`add-${resource}-btn`} onClick={openNew} className="rounded-sm bg-brand-orange hover:bg-brand-orange-600">
          <Plus className="mr-1 h-4 w-4" /> Add {config.singular}
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-brand-orange" /></div>
      ) : (
        <div className="overflow-hidden rounded-sm border border-border bg-white">
          <table className="w-full text-sm" data-testid={`table-${resource}`}>
            <thead className="bg-secondary/60 text-left">
              <tr>
                {config.reorder && <th className="w-16 px-4 py-3" />}
                {config.list.map((k) => <th key={k} className="px-4 py-3 font-display font-semibold uppercase tracking-wide text-xs text-navy">{k}</th>)}
                <th className="px-4 py-3 text-right font-display text-xs font-semibold uppercase tracking-wide text-navy">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={config.list.length + 2} className="px-4 py-10 text-center text-muted-foreground">No items yet. Click "Add {config.singular}".</td></tr>
              )}
              {items.map((item, i) => (
                <tr key={item.id} className="border-t border-border hover:bg-secondary/30">
                  {config.reorder && (
                    <td className="px-4 py-3">
                      <div className="flex flex-col">
                        <button onClick={() => move(i, -1)} className="text-muted-foreground hover:text-navy"><ArrowUp className="h-3.5 w-3.5" /></button>
                        <button onClick={() => move(i, 1)} className="text-muted-foreground hover:text-navy"><ArrowDown className="h-3.5 w-3.5" /></button>
                      </div>
                    </td>
                  )}
                  {config.list.map((k) => <td key={k} className="px-4 py-3">{cellValue(item, k)}</td>)}
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" title={item.status === "published" ? "Unpublish" : "Publish"} onClick={() => toggleStatus(item)} data-testid={`toggle-${item.id}`}>
                        {item.status === "published" ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4 text-green-600" />}
                      </Button>
                      <Button variant="ghost" size="icon" title="Edit" onClick={() => openEdit(item)} data-testid={`edit-${item.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" title="Delete" onClick={() => setDeleteId(item.id)} data-testid={`delete-${item.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? `Edit ${config.singular}` : `New ${config.singular}`}</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <DynamicForm fields={config.fields} values={values} onChange={(k, v) => setValues((s) => ({ ...s, [k]: v }))} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-resource-btn" className="rounded-sm bg-navy hover:bg-navy-surface">
              {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this {config.singular.toLowerCase()}?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="confirm-delete-btn" className="rounded-sm bg-destructive hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ResourceManager;
